import axios from "axios";
import { store } from "../app/store";
import {
  AcceptDocumentsBody,
  AvailableRiskProfilesResponse,
  CreateAccountResponse,
  PromoterResponse,
  QuestionnaireResponse,
  QuestionnaireStatusResponse,
  QuestionnaireSubmissionBody,
  QuestionnaireSubmissionResponse,
  RegisterBody,
  RegisterResponse,
} from "../types/interactiveBrokers";

const BASE_URL = `${import.meta.env.VITE_BASE_URL}`;

const axiosWithToken = axios.create({
  baseURL: BASE_URL,
});

axiosWithToken.interceptors.request.use((config) => {
  const state = store.getState();
  const token = state.authentication.token;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

export const getPromoters = () => {
  return axiosWithToken.get<PromoterResponse>(
    `${BASE_URL}/api/promoters/active/`
  );
};

export const requestEmailVerification = ({
  email,
  language_preference,
}: {
  email: string;
  language_preference: "es" | "en";
}) => {
  return axios.post(`${BASE_URL}/api/users/request-email-verification/`, {
    email,
    language_preference,
  });
};

export const verifyEmail = ({
  email,
  token,
}: {
  email: string;
  token: string;
}) => {
  return axios.post(`${BASE_URL}/api/users/verify-email/`, {
    email,
    token,
  });
};

export const registerWithDocuments = (body: RegisterBody) => {
  return axiosWithToken.post<RegisterResponse>(
    `${BASE_URL}/api/users/register-with-documents/`,
    body
  );
};

export const updatePersonalInformation = (body: RegisterBody) => {
  return axiosWithToken.patch<RegisterResponse>(
    `${BASE_URL}/api/users/register-with-documents/`,
    body
  );
};

export const acceptDocuments = (body: AcceptDocumentsBody) => {
  return axiosWithToken.post(
    `${BASE_URL}/api/users/bulk-document-acceptance/`,
    body
  );
};

export const getActiveQuestionnaire = () => {
  return axiosWithToken.get<QuestionnaireResponse>(
    `${BASE_URL}/api/investor-questionnaire/questionnaires/active/`
  );
};

export const submitQuestionnaire = (body: QuestionnaireSubmissionBody) => {
  return axiosWithToken.post<QuestionnaireSubmissionResponse>(
    `${BASE_URL}/api/investor-questionnaire/user-questionnaires/`,
    body
  );
};

export const getQuestionnaireStatus = () => {
  return axiosWithToken.get<QuestionnaireStatusResponse>(
    `${BASE_URL}/api/investor-questionnaire/user-questionnaires/check-status/`
  );
};

export const acceptRiskProfile = ({
  id,
  accept,
}: {
  id: number;
  accept: boolean;
}) => {
  return axiosWithToken.post(
    `${BASE_URL}/api/investor-questionnaire/user-questionnaires/${id}/accept_risk_profile/`,
    {
      accept,
    }
  );
};

export const getAvailableRiskProfiles = () => {
  return axiosWithToken.get<AvailableRiskProfilesResponse>(
    `${BASE_URL}/api/investor-questionnaire/risk-profiles/`
  );
};

export const createAccount = ({ user_id }: { user_id: number }) => {
  return axiosWithToken.post<CreateAccountResponse>(
    `${BASE_URL}/api/ib/create-account-internal/`,
    {
      user_id,
    }
  );
};
